import React from 'react';
import { Button, Row, Col, Form, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Save, Mail, Send, CheckCircle, RefreshCw, Bot, BookCheck, ScanEye, Info, Zap } from 'lucide-react';

// AI agent (Finn) settings section — approver/notification channel and the
// auto-approval rule thresholds. State, fetch and save live in the
// CompanySettings shell (lazy-loaded on first visit) and arrive as props.
const AgentsSection = ({
  agentSettings,
  setAgentSettings,
  saveAgentSettings,
  agentSettingsLoading,
  agentSettingsSaving,
}) => {
  if (agentSettingsLoading) {
    return (
      <div className="text-center py-5">
        <RefreshCw className="spin" size={24} />
        <p className="text-muted mt-2">Laddar inställningar...</p>
      </div>
    );
  }

  return (
    <>
      {/* Hero header */}
      <div className="modern-card" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)', border: '1px solid #d0daf0' }}>
        <div className="modern-card-body" style={{ padding: '24px 28px' }}>
          <div className="d-flex align-items-start justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: '#dbeafe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#2563eb'
              }}>
                <Bot size={26} />
              </div>
              <div>
                <h2 className="mb-1" style={{ fontSize: '1.35rem', fontWeight: 600 }}>
                  Finn &mdash; Fakturaagent
                </h2>
                <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
                  Din AI-assistent som tar emot, tolkar och bokför leverantörsfakturor automatiskt
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={saveAgentSettings}
              disabled={agentSettingsSaving}
              style={{ minWidth: 110 }}
            >
              <Save className="me-2" size={15} />
              {agentSettingsSaving ? 'Sparar...' : 'Spara'}
            </Button>
          </div>
        </div>
      </div>

      {/* The forwarding-address card moved to the E-postinkorg tab
          (it's an email intake channel) — see ForwardingAddresses. */}

      {/* How it works */}
      <div className="modern-card mt-3">
        <div className="modern-card-body" style={{ padding: '20px 28px' }}>
          <h6 className="text-muted mb-3" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Hur det fungerar
          </h6>
          <Row>
            {[
              { icon: <Mail size={20} />, step: '1', title: 'Ta emot', desc: 'Vidarebefordra fakturor via e-post till din unika adress' },
              { icon: <ScanEye size={20} />, step: '2', title: 'Tolka', desc: 'Finn läser av fakturan med OCR och AI' },
              { icon: <CheckCircle size={20} />, step: '3', title: 'Granska', desc: 'Du godkänner eller justerar bokföringsförslaget' },
              { icon: <BookCheck size={20} />, step: '4', title: 'Bokför', desc: 'Verifikatet skapas automatiskt i din bokföring' },
            ].map(({ icon, step, title, desc }) => (
              <Col xs={6} md={3} key={step} className="text-center mb-3 mb-md-0">
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: '#f3f4f6', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: 8, color: 'var(--primary)'
                }}>
                  {icon}
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.4 }}>{desc}</div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      <Row className="mt-3">
        {/* Left column: Approver & notifications */}
        <Col lg={6}>
          <div className="modern-card h-100" id="godkannare">
            <div className="modern-card-body" style={{ padding: '24px 28px' }}>
              <div className="d-flex align-items-center gap-2 mb-4">
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: '#dbeafe', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: '#2563eb'
                }}>
                  <Mail size={16} />
                </div>
                <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600 }}>Godkännare & notifikationer</h5>
              </div>

              <Form.Group className="mb-4">
                <div className="d-flex align-items-center gap-1">
                  <Form.Label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 500 }}>E-post för godkännande</Form.Label>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Hit skickas fakturor som behöver manuellt godkännande</Tooltip>}>
                    <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                  </OverlayTrigger>
                </div>
                <Form.Control
                  type="email"
                  value={agentSettings.approverEmail}
                  onChange={(e) => setAgentSettings(prev => ({ ...prev, approverEmail: e.target.value }))}
                  placeholder="namn@foretag.se"
                  style={{ borderRadius: 8, marginTop: 6 }}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Notifikationskanal</Form.Label>
                <div className="d-flex flex-column gap-2">
                  {[
                    { value: 'email', label: 'E-post', icon: <Mail size={16} />, disabled: false },
                    { value: 'slack', label: 'Slack', icon: <Send size={16} />, disabled: true, badge: 'Kommer snart' },
                    { value: 'teams', label: 'Microsoft Teams', icon: <Send size={16} />, disabled: true, badge: 'Kommer snart' },
                  ].map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => !opt.disabled && setAgentSettings(prev => ({ ...prev, approvalChannel: opt.value }))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', borderRadius: 8,
                        border: `1.5px solid ${agentSettings.approvalChannel === opt.value ? 'var(--primary)' : '#e5e7eb'}`,
                        background: agentSettings.approvalChannel === opt.value ? '#f0f4ff' : opt.disabled ? '#fafafa' : 'white',
                        cursor: opt.disabled ? 'default' : 'pointer',
                        opacity: opt.disabled ? 0.55 : 1,
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <span style={{ color: agentSettings.approvalChannel === opt.value ? 'var(--primary)' : '#9ca3af' }}>{opt.icon}</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{opt.label}</span>
                      {opt.badge && (
                        <Badge bg="light" text="muted" style={{ fontSize: '0.7rem', marginLeft: 'auto' }}>{opt.badge}</Badge>
                      )}
                      {agentSettings.approvalChannel === opt.value && !opt.disabled && (
                        <CheckCircle size={16} style={{ marginLeft: 'auto', color: 'var(--primary)' }} />
                      )}
                    </div>
                  ))}
                </div>
              </Form.Group>
            </div>
          </div>
        </Col>

        {/* Right column: Auto-approval rules */}
        <Col lg={6} className="mt-3 mt-lg-0">
          <div className="modern-card h-100" id="auto-godkannande">
            <div className="modern-card-body" style={{ padding: '24px 28px' }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: '#fef3c7', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: '#d97706'
                }}>
                  <Zap size={16} />
                </div>
                <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600 }}>Automatiskt godkännande</h5>
              </div>
              <p className="text-muted mb-4" style={{ fontSize: '0.78rem' }}>
                Fakturor som uppfyller <strong>alla</strong> villkor nedan godkänns utan manuell granskning
              </p>

              <Form.Group className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-1">
                    <Form.Label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 500 }}>Maxbelopp</Form.Label>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Fakturor över detta belopp kräver alltid manuellt godkännande. Sätt till 0 för att inaktivera.</Tooltip>}>
                      <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                    </OverlayTrigger>
                  </div>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--primary)' }}>
                    {Number(agentSettings.autoApproveMaxAmountSek).toLocaleString('sv-SE')} kr
                  </span>
                </div>
                <Form.Control
                  type="number"
                  min="0"
                  step="500"
                  value={agentSettings.autoApproveMaxAmountSek}
                  onChange={(e) => setAgentSettings(prev => ({ ...prev, autoApproveMaxAmountSek: Number(e.target.value) }))}
                  style={{ borderRadius: 8 }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-1">
                    <Form.Label className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 500 }}>Minsta OCR-konfidensgrad</Form.Label>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Fakturan måste ha minst denna konfidensgrad från OCR-tolkningen för att godkännas automatiskt</Tooltip>}>
                      <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                    </OverlayTrigger>
                  </div>
                  <Badge
                    bg={agentSettings.autoApproveMinConfidence >= 0.9 ? 'success' : agentSettings.autoApproveMinConfidence >= 0.7 ? 'warning' : 'danger'}
                    style={{ minWidth: '52px', fontSize: '0.8rem', fontWeight: 600 }}
                  >
                    {Math.round(agentSettings.autoApproveMinConfidence * 100)}%
                  </Badge>
                </div>
                <Form.Range
                  min={0}
                  max={1}
                  step={0.05}
                  value={agentSettings.autoApproveMinConfidence}
                  onChange={(e) => setAgentSettings(prev => ({ ...prev, autoApproveMinConfidence: Number(e.target.value) }))}
                />
                <div className="d-flex justify-content-between" style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: -4 }}>
                  <span>Låg</span>
                  <span>Hög</span>
                </div>
              </Form.Group>

              <div style={{
                padding: '14px 16px', borderRadius: 10,
                background: '#f9fafb', border: '1px solid #f3f4f6'
              }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>Kräv känd leverantör</div>
                    </div>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Okända leverantörer skickas alltid till manuell granskning</Tooltip>}>
                      <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                    </OverlayTrigger>
                  </div>
                  <Form.Check
                    type="switch"
                    id="auto-approve-known-supplier"
                    checked={agentSettings.autoApproveKnownSupplierOnly}
                    onChange={(e) => setAgentSettings(prev => ({ ...prev, autoApproveKnownSupplierOnly: e.target.checked }))}
                  />
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default AgentsSection;
